import React, { useEffect, useMemo, useState } from "react";
import { AlertTriangle, Check, Database, Loader2, MapPin, Search, Sparkles, Users } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { QuizQuestion } from "./QuizShell";
import { OptionCard } from "./OptionCard";
import type { QuizProgress } from "./types";

type Screen = "who" | "state" | "hubs" | "myhubs";

export interface AudienceQuizProps {
  progress: QuizProgress;
  onTotal: (n: number) => void;
  sendTo: { database: boolean; mycontacts: boolean };
  setSendTo: React.Dispatch<React.SetStateAction<{ database: boolean; mycontacts: boolean }>>;
  hasPlan: boolean;
  isOnFreeTrial: boolean;
  hasHubAccess: boolean;
  openCampaignAddOn: () => void;
  openUpgrade: () => void;
  stateOptions: { value: string; label: string }[];
  statesLoading: boolean;
  selectedState: string;
  setSelectedState: (v: string) => void;
  agentLists: any[];
  loadingStateAssociations: boolean;
  countySuggestions: string[];
  suggestingCounties: boolean;
  selectedDatasets: (string | number)[];
  setSelectedDatasets: (v: any) => void;
  onAddDataset: (id: string | number) => void;
  onRemoveDataset: (id: string | number) => void;
  customAgentLists: any[];
  selectedCustomDatasets: (string | number)[];
  setSelectedCustomDatasets: (v: any) => void;
  onAddCustom: (id: string | number) => void;
  onRemoveCustom: (id: string | number) => void;
  user: any;
  planLoading: boolean;
  /** Runs the classic validation (trial limit etc.) and moves to step 6. */
  onFinish: () => void;
  onBack: () => void;
  onManageHubs: () => void;
}

const countOf = (l: any) => Number(l.agent_count ?? l.count ?? 0);

export const AudienceQuiz: React.FC<AudienceQuizProps> = (p) => {
  const {
    progress,
    onTotal,
    sendTo,
    setSendTo,
    hasPlan,
    isOnFreeTrial,
    hasHubAccess,
    selectedDatasets,
    selectedCustomDatasets,
  } = p;
  const [screen, setScreen] = useState<Screen>("who");
  const [stateQuery, setStateQuery] = useState("");
  const [hubQuery, setHubQuery] = useState("");

  const dbOn = sendTo.database && hasPlan && !isOnFreeTrial;
  const mcOn = sendTo.mycontacts && hasHubAccess;

  const flow: Screen[] = useMemo(
    () => ["who", ...(dbOn ? (["state", "hubs"] as Screen[]) : []), ...(mcOn ? (["myhubs"] as Screen[]) : [])],
    [dbOn, mcOn]
  );
  const idx = Math.max(0, flow.indexOf(screen));
  const position = progress.before + idx + 1;
  const total = progress.before + flow.length + progress.after;

  useEffect(() => {
    onTotal(flow.length);
  }, [flow.length, onTotal]);

  const isLast = idx === flow.length - 1;
  const goNext = () => (isLast ? p.onFinish() : setScreen(flow[idx + 1]));
  const goBack = () => (idx === 0 ? p.onBack() : setScreen(flow[idx - 1]));

  const dbSelected = (id: any) => selectedDatasets.some((x) => String(x) === String(id));
  const mcSelected = (id: any) => selectedCustomDatasets.some((x) => String(x) === String(id));
  const q = { position, total } as const;

  const toggleDatabase = () => {
    if (!hasPlan) {
      p.openCampaignAddOn();
      return;
    }
    if (isOnFreeTrial) return;
    setSendTo((s) => {
      const next = !s.database;
      if (!next) p.setSelectedDatasets([]);
      return { ...s, database: next };
    });
  };

  const toggleHubs = () => {
    if (!hasHubAccess) {
      p.openUpgrade();
      return;
    }
    setSendTo((s) => {
      const next = !s.mycontacts;
      if (!next) p.setSelectedCustomDatasets([]);
      return { ...s, mycontacts: next };
    });
  };

  if (screen === "who") {
    const inTrial = p.user?.is_in_active_trial;
    const remaining = p.user?.remaining_trial_campaigns ?? 0;
    const limit = p.user?.trial_campaigns_limit ?? 2;
    return (
      <QuizQuestion
        {...q}
        title="Who should receive this launch?"
        hint="Pick as many as you want."
        onBack={p.onBack}
        onContinue={goNext}
        continueDisabled={!dbOn && !mcOn}
        continueLoading={p.planLoading && flow.length === 1}
        size="md"
      >
        {inTrial && (
          <div className="mb-5 flex items-start gap-3 rounded-2xl border border-orange-200 bg-orange-50 p-4 text-sm text-orange-900">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-orange-600" />
            <div>
              <p className="font-semibold">
                Free trial — {remaining} of {limit} launches remaining
              </p>
              <p className="mt-0.5 text-xs text-orange-700">
                During your trial, sending is limited to My Hubs.
              </p>
            </div>
          </div>
        )}
        <div className="grid gap-4 sm:grid-cols-2">
          <OptionCard
            icon={Database}
            tone="blue"
            title="LaunchIQ Database"
            description="Reach agents organised by state and county hubs."
            selected={dbOn}
            disabled={isOnFreeTrial}
            badge={
              !hasPlan ? (
                <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Requires plan</Badge>
              ) : isOnFreeTrial ? (
                <Badge className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100">Trial: use My Hubs</Badge>
              ) : undefined
            }
            onClick={toggleDatabase}
          />
          <OptionCard
            icon={Users}
            tone="emerald"
            title="My Hubs"
            description="Send to your own contact groups."
            selected={mcOn}
            locked={!hasHubAccess}
            badge={
              !hasHubAccess ? (
                <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100">Pro feature</Badge>
              ) : undefined
            }
            onClick={toggleHubs}
          />
        </div>
        {!hasHubAccess && (
          <div className="mt-5 flex items-start gap-3 rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm text-blue-900">
            <Sparkles className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p>
                <strong>Want to send to your own contacts?</strong> Upgrade to Pro to unlock My Hubs.
              </p>
              <Button variant="link" className="h-auto p-0 text-blue-700 underline" onClick={p.openUpgrade}>
                View plans →
              </Button>
            </div>
          </div>
        )}
      </QuizQuestion>
    );
  }

  if (screen === "state") {
    const filtered = p.stateOptions.filter((s) =>
      s.label.toLowerCase().includes(stateQuery.toLowerCase())
    );
    return (
      <QuizQuestion
        {...q}
        title="Which state do you want to reach?"
        hint="Hubs organise agents by location — pick a state to see its hubs."
        onBack={goBack}
        onContinue={p.selectedState ? goNext : undefined}
        size="lg"
      >
        <div className="relative mx-auto mb-5 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={stateQuery}
            onChange={(e) => setStateQuery(e.target.value)}
            placeholder="Search states…"
            className="h-11 rounded-full pl-9"
          />
        </div>
        {p.statesLoading ? (
          <div className="flex justify-center py-10">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : filtered.length === 0 ? (
          <p className="py-8 text-center text-slate-500">No states found</p>
        ) : (
          <div className="grid max-h-[460px] grid-cols-2 gap-3 overflow-y-auto pr-1 sm:grid-cols-3 lg:grid-cols-4">
            {filtered.map((s) => (
              <OptionCard
                key={s.value}
                layout="row"
                icon={MapPin}
                tone="blue"
                title={s.label}
                selected={String(p.selectedState) === s.value}
                onClick={() => {
                  p.setSelectedState(s.value);
                  setScreen("hubs");
                }}
              />
            ))}
          </div>
        )}
      </QuizQuestion>
    );
  }

  if (screen === "hubs") {
    const seen = new Set<string>();
    const all = p.agentLists
      .filter((l) => {
        const id = String(l.id);
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      })
      .filter((l) => String(l.name).toLowerCase().includes(hubQuery.toLowerCase()));
    const suggestions = p.countySuggestions
      .map((name) => p.agentLists.find((l) => l.name === name))
      .filter(Boolean) as any[];
    const stateLabel = p.stateOptions.find((s) => s.value === String(p.selectedState))?.label;

    const hubCard = (l: any, accent = false) => {
      const id = String(l.id);
      const sel = dbSelected(id);
      return (
        <div
          key={`${accent ? "s" : "h"}-${id}`}
          className={cn(
            "relative flex flex-col gap-3 rounded-2xl border bg-white p-4 shadow-sm transition",
            sel ? "border-blue-500 ring-2 ring-blue-200" : accent ? "border-amber-200 hover:border-amber-300" : "border-slate-200 hover:border-blue-300"
          )}
        >
          {sel && (
            <span className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-white shadow">
              <Check className="h-4 w-4" />
            </span>
          )}
          <div>
            <p className="break-words text-sm font-semibold text-slate-900">{l.name}</p>
            <p className="mt-1 inline-flex items-center gap-1 rounded-lg bg-slate-100 px-2 py-1 text-xs font-medium text-slate-600">
              <Users className="h-3.5 w-3.5" />
              {countOf(l).toLocaleString()} agents
            </p>
          </div>
          <Button
            size="sm"
            onClick={() => (sel ? p.onRemoveDataset(id) : p.onAddDataset(id))}
            className={cn(
              "mt-auto w-full text-white",
              sel ? "bg-red-500 hover:bg-red-600" : "bg-blue-600 hover:bg-blue-700"
            )}
          >
            {sel ? "Remove" : "Add"}
          </Button>
        </div>
      );
    };

    return (
      <QuizQuestion
        {...q}
        title={stateLabel ? `Choose your hubs in ${stateLabel}` : "Choose your hubs"}
        hint="Pick as many as you want. One hub is included with every launch; extra hubs may be charged."
        onBack={goBack}
        onContinue={goNext}
        continueDisabled={selectedDatasets.length === 0}
        continueLoading={isLast && p.planLoading}
        summary={`${selectedDatasets.length} hub${selectedDatasets.length === 1 ? "" : "s"} selected`}
        size="xl"
      >
        {p.suggestingCounties && (
          <div className="mb-5 flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 text-sm font-medium text-amber-900">
            <Loader2 className="h-5 w-5 animate-spin text-amber-600" />
            Finding top 3 nearby hubs for your property…
          </div>
        )}
        {!p.suggestingCounties && suggestions.length > 0 && (
          <div className="mb-6">
            <p className="mb-3 flex items-center gap-2 text-sm font-semibold text-amber-900">
              <Sparkles className="h-4 w-4 text-amber-600" /> Top 3 suggested hubs near your property
            </p>
            <div className="grid gap-3 sm:grid-cols-3">{suggestions.map((l) => hubCard(l, true))}</div>
          </div>
        )}

        <div className="relative mx-auto mb-5 max-w-md">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <Input
            value={hubQuery}
            onChange={(e) => setHubQuery(e.target.value)}
            placeholder="Search hubs…"
            className="h-11 rounded-full pl-9"
          />
        </div>

        {p.loadingStateAssociations ? (
          <div className="flex items-center justify-center gap-3 rounded-2xl border border-blue-200 bg-blue-50 p-8 text-blue-900">
            <Loader2 className="h-5 w-5 animate-spin" />
            <div>
              <p className="text-sm font-medium">Loading hubs…</p>
              <p className="text-xs text-blue-700">This may take a few seconds</p>
            </div>
          </div>
        ) : all.length === 0 ? (
          <p className="py-8 text-center text-slate-500">No hubs found for this state.</p>
        ) : (
          <div className="grid max-h-[480px] gap-3 overflow-y-auto pr-1 sm:grid-cols-2 lg:grid-cols-3">
            {all.map((l) => hubCard(l))}
          </div>
        )}
      </QuizQuestion>
    );
  }

  // my hubs
  return (
    <QuizQuestion
      {...q}
      title="Which of your hubs should get it?"
      hint="Select one or more of your contact groups."
      onBack={goBack}
      onContinue={goNext}
      continueDisabled={selectedCustomDatasets.length === 0}
      continueLoading={p.planLoading}
      summary={`${selectedCustomDatasets.length} hub${selectedCustomDatasets.length === 1 ? "" : "s"} selected`}
      size="lg"
    >
      {p.customAgentLists.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-white p-10 text-center">
          <Users className="mx-auto mb-3 h-12 w-12 text-slate-300" />
          <p className="font-semibold text-slate-700">You haven't created any contact groups yet</p>
          <p className="mt-1 text-sm text-slate-500">Add contacts in My Hub, then come back to your launch.</p>
          <Button className="mt-5" onClick={p.onManageHubs}>
            Go to My Hub
          </Button>
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2">
          {p.customAgentLists.map((l) => {
            const sel = mcSelected(l.id);
            return (
              <OptionCard
                key={l.id}
                layout="row"
                icon={Users}
                tone="emerald"
                title={l.name}
                description={`${countOf(l).toLocaleString()} contacts`}
                selected={sel}
                onClick={() => (sel ? p.onRemoveCustom(l.id) : p.onAddCustom(l.id))}
              />
            );
          })}
        </div>
      )}
    </QuizQuestion>
  );
};
